/**
 * Student controls state machine.
 *
 * Spec §5.5 modes:
 *   - invisible (default): subscribe-only, no publish, no presence broadcast.
 *   - visible: publishes `presence` on entry; raise-hand/audio/video controls enabled.
 *
 * Raise hand: amber LED for up to 60s; auto-clears or is cleared by audio_on /
 * video_on / invisible-mode transition.
 *
 * This module is the pure store. Side effects (publish data, publish tracks)
 * happen in Class.svelte calling these via a `StudentControlsBridge`.
 */
import { writable, type Writable } from "svelte/store";
import {
  publishEnvelope,
  type DcType,
  type RaiseHandClearPayload,
  type PublishFn,
} from "@diy-avatar/shared";

export type StudentMode = "invisible" | "visible";

export interface StudentControlsState {
  mode: StudentMode;
  raiseHandActive: boolean;
  audioOn: boolean;
  videoOn: boolean;
  textOpen: boolean;
}

const initial: StudentControlsState = {
  mode: "invisible",
  raiseHandActive: false,
  audioOn: false,
  videoOn: false,
  textOpen: false,
};

export const RAISE_HAND_TIMEOUT_MS = 60_000;

export interface ControlsBridge {
  /** publish data-channel envelope */
  publish: PublishFn;
  /** publish/unpublish the student mic track */
  setAudioPublishing: (on: boolean) => Promise<void> | void;
  /** publish/unpublish the student camera track */
  setVideoPublishing: (on: boolean) => Promise<void> | void;
  /** when the raise-hand auto-clear timer fires; defaults to setTimeout */
  setTimer?: (cb: () => void, ms: number) => number;
  clearTimer?: (id: number) => void;
}

export class StudentControls {
  readonly store: Writable<StudentControlsState>;
  private state: StudentControlsState = { ...initial };
  private raiseHandTimerId: number | null = null;
  private bridge: ControlsBridge;
  /** When true, the student is never allowed to enter invisible mode. */
  private invisibleForbidden = false;

  constructor(bridge: ControlsBridge, opts?: { invisibleForbidden?: boolean }) {
    this.bridge = bridge;
    this.invisibleForbidden = opts?.invisibleForbidden ?? false;
    this.store = writable(this.state);
  }

  /** Capability flag: when true, `setMode("invisible")` becomes a no-op. */
  setInvisibleForbidden(v: boolean): void {
    this.invisibleForbidden = v;
  }

  private emit() {
    this.store.set({ ...this.state });
  }

  private send<T extends DcType, P>(type: T, payload: P) {
    publishEnvelope(this.bridge.publish, type, payload);
  }

  /** Switch to visible mode: publishes `presence { mode: "visible" }`. */
  async setMode(mode: StudentMode, full_name?: string): Promise<void> {
    // Capability guard: a forbidden student can never go invisible. No state
    // change, no presence broadcast — silently ignore any caller.
    if (mode === "invisible" && this.invisibleForbidden) return;
    if (this.state.mode === mode) return;
    if (mode === "invisible") {
      // Tear down anything that requires visible.
      if (this.state.raiseHandActive) this.clearRaiseHand("manual");
      if (this.state.audioOn) {
        await this.bridge.setAudioPublishing(false);
        this.state.audioOn = false;
        this.send("audio_off", {});
      }
      if (this.state.videoOn) {
        await this.bridge.setVideoPublishing(false);
        this.state.videoOn = false;
        this.send("video_off", {});
      }
      this.state.mode = "invisible";
      this.send("presence", { mode: "invisible", full_name });
    } else {
      this.state.mode = "visible";
      this.send("presence", { mode: "visible", full_name });
    }
    this.emit();
  }

  /** Raise hand: only valid in visible mode. */
  raiseHand(full_name?: string): void {
    if (this.state.mode !== "visible") return;
    if (this.state.raiseHandActive) return;
    this.state.raiseHandActive = true;
    this.send("raise_hand", { full_name });
    const setTimer = this.bridge.setTimer ?? ((cb, ms) => setTimeout(cb, ms) as unknown as number);
    this.raiseHandTimerId = setTimer(() => {
      this.clearRaiseHand("timeout");
    }, RAISE_HAND_TIMEOUT_MS);
    this.emit();
  }

  /** Manually lower a raised hand. No-op if hand not currently raised. */
  lowerHand(): void {
    if (!this.state.raiseHandActive) return;
    this.clearRaiseHand("manual");
  }

  private clearRaiseHand(reason: RaiseHandClearPayload["reason"]): void {
    if (!this.state.raiseHandActive) return;
    this.state.raiseHandActive = false;
    if (this.raiseHandTimerId != null) {
      const clearTimer = this.bridge.clearTimer ?? ((id: number) => clearTimeout(id as unknown as ReturnType<typeof setTimeout>));
      clearTimer(this.raiseHandTimerId);
      this.raiseHandTimerId = null;
    }
    this.send("raise_hand_clear", { reason });
    this.emit();
  }

  async setAudio(on: boolean): Promise<void> {
    if (this.state.mode !== "visible") return;
    if (this.state.audioOn === on) return;
    await this.bridge.setAudioPublishing(on);
    this.state.audioOn = on;
    if (on && this.state.raiseHandActive) this.clearRaiseHand("audio");
    this.send(on ? "audio_on" : "audio_off", {});
    this.emit();
  }

  async setVideo(on: boolean): Promise<void> {
    if (this.state.mode !== "visible") return;
    if (this.state.videoOn === on) return;
    await this.bridge.setVideoPublishing(on);
    this.state.videoOn = on;
    if (on && this.state.raiseHandActive) this.clearRaiseHand("video");
    this.send(on ? "video_on" : "video_off", {});
    this.emit();
  }

  openText(): void {
    if (this.state.textOpen) return;
    this.state.textOpen = true;
    this.send("text_open", {});
    this.emit();
  }

  sendText(text: string, full_name?: string): void {
    if (!this.state.textOpen) return;
    if (!text) return;
    this.send("text_send", { text, full_name });
  }

  /** Called when the student hits "close" on their textarea. */
  closeTextByStudent(): void {
    if (!this.state.textOpen) return;
    this.state.textOpen = false;
    this.send("text_close", { by: "student" });
    this.emit();
  }

  /** Called when the device-side teacher key-press arrives. */
  closeTextByTeacher(): void {
    if (!this.state.textOpen) return;
    this.state.textOpen = false;
    // Note: do NOT echo a text_close back — teacher already sent one.
    this.emit();
  }

  /** For inspection / tests. */
  snapshot(): StudentControlsState {
    return { ...this.state };
  }
}
